import { PropertyValues } from 'lit';
import { PreventAndRedirectCommands, PreventCommands, Router, RouterLocation } from '@vaadin/router';
import { property, state } from 'lit/decorators.js';
import { MetaOptions, updateMeta } from './meta-manager';
import { appOutOfDate, outOfDateURL } from '../../debug';
import {
  addCurrentUserEventListener,
  CurrentUserStateNotifier,
  getCurrentUser,
  removeCurrentUserEventListener,
  UserPublicInfo
} from '../../api/current-user';
import { IDispose } from '../../dispose';
import { router } from '../../router';
import { NullPromise } from '../events';
import { LitElementBase } from '../../litelement-base';

export interface IPageController {
  pagesCanClose: () => Promise<boolean>;

  disposeAndRefresh();

  disposeAll();
}

interface IPageRefresh {
  disposeAndRefresh: () => Promise<void>;
  dispose: () => Promise<void>;
  pageCanClose: () => Promise<boolean>;
}

class PageController implements IPageController {
  pages: IPageRefresh[] = [];

  addPage(page: IPageRefresh) {
    if (!this.pages.find(x => x == page)) this.pages.push(page);
  }

  remPage(page: IPageRefresh) {
    this.pages = this.pages.filter(x => x !== page);
  }

  async disposeAndRefresh() {
    for (let i = 0; i < this.pages.length; i++) await this.pages[i].disposeAndRefresh();
  }

  async disposeAll() {
    for (let i = 0; i < this.pages.length; i++) await this.pages[i].dispose();
  }

  async pagesCanClose(): Promise<boolean> {
    let canClose = true;
    for (let i = 0; i < this.pages.length; i++) canClose = (await this.pages[i].pageCanClose()) && canClose;
    return canClose;
  }
}

const pageController = new PageController();

export function getPageController(): IPageController {
  return pageController;
  //allow refreshing
}

export class PageBaseInternal extends LitElementBase implements CurrentUserStateNotifier, IPageRefresh, IDispose {
  @property({ type: Object })
  protected location = router.location;
  @state()
  protected loggedIn = false;
  protected defaultTitleTemplate = `%s | Dealer Module`;

  protected get defaultMeta() {
    return {
      url: window.location.href,
      titleTemplate: this.defaultTitleTemplate
    };
  }

  async onBeforeEnter(_location: RouterLocation, commands: PreventAndRedirectCommands, _router: Router): Promise<any> {
    this.loggedIn = getCurrentUser() !== null;
    if (appOutOfDate() && this.divertToOutOfDate()) {
      window.location.reload();
      return commands.redirect(outOfDateURL);
    }
    if (!this.loggedIn && this.divertToLogin()) {
      return commands.redirect('/login');
    }

    const allowEnter = await this.allowEnter();
    if (!allowEnter) {
      const r = await this.redirectPath();
      if (r) return commands.redirect(r);
      return commands.prevent();
    }
    pageController.addPage(this);
  }

  async redirectPath(): NullPromise<string> {
    return null;
  }

  async pageCanClose(): Promise<boolean> {
    if (this.loggedIn) return await this.canLeavePage();
    return true;
  }

  async onBeforeLeave(_location: RouterLocation, commands: PreventCommands, _router: Router) {
    if (appOutOfDate()) return true;
    if (this.loggedIn)
      if (!(await this.canLeavePage())) {
        //if not logged in, we cant do anything, we are trying to exit cleanly
        return commands.prevent();
      }
    pageController.remPage(this);
    return true;
  }

  public async onAfterEnter(
    _location: RouterLocation,
    _commands: PreventAndRedirectCommands,
    _router: Router
  ): Promise<void> {
    const event = new CustomEvent('webmodule-page-navigation', {
      bubbles: true,
      composed: true,
      detail: { name: _location.route?.name, path: _location.route?.path }
    });
    this.dispatchEvent(event);
    await this.awaken();
  }

  public async onAfterLeave(
    _location: RouterLocation,
    _commands: PreventAndRedirectCommands,
    _router: Router
  ): Promise<void> {
    if (this.loggedIn) {
      //if we are not logged in, then anything we do here is probably going to fail as we are doing a page reload.
      await this.dispose();
    }
  }

  public async disposeAndRefresh() {
    await this.dispose();
    await this.afterUserConnected();
  }

  public async dispose() {
    //do shutdown stuff
  }

  async userReset() {
    await this.dispose();
  }

  async loadUserCoreDetails() {
    throw new Error('loadUserCoreDetails');
  }

  async userStateChanged(user: UserPublicInfo | null): Promise<void> {
    if (appOutOfDate()) return;
    this.loggedIn = user !== null;
    if (user !== null) {
      await this.loadUserCoreDetails();

      await this.afterUserConnected();
    } else {
      await this.userReset();
    }
    this.requestUpdate();
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('ui-changed', this.eventChildUIChanged);
    addCurrentUserEventListener(this);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('ui-changed', this.eventChildUIChanged);
    removeCurrentUserEventListener(this);
  }

  updated(changedProperties: PropertyValues<this>) {
    super.updated(changedProperties);

    const meta = this.meta();

    if (meta) {
      updateMeta({
        ...this.defaultMeta,
        ...((meta.titleTemplate || meta.titleTemplate === null) && {
          titleTemplate: meta.titleTemplate
        }),
        ...meta
      });
    }
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  protected divertToLogin(): boolean {
    return true;
  }

  protected divertToOutOfDate(): boolean {
    return true;
  }

  /***
   * overriding this can be used as a preconstruction technique that can fail if for some reason the page
   * cannot be accessed.. or redirected.
   */
  protected async allowEnter(): Promise<boolean> {
    return true;
  }

  protected async canLeavePage(): Promise<boolean> {
    return true;
  }

  protected async awaken() {
    //do stuff as preload.
  }

  /**
   * The page must override this method to customize the meta
   */
  protected meta(): MetaOptions | undefined {
    return;
  }

  protected async afterUserConnected() {
    //
  }

  protected eventChildUIChanged = (_e: Event) => this.requestUpdate();
}
